import { useEffect, useMemo, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { flatten } from 'lodash';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { getProducts } from '../../api/Product';

type ProductsDropdownProps = {
  label?: string;
  disabled?: boolean;
  excludedIDs?: number[];
} & (
  | {
      multiple: false | undefined;
      value?: number;
      onChange: (id?: number) => void;
    }
  | {
      multiple: true;
      value: number[];
      onChange: (ids: number[]) => void;
    }
);

export const ProductsDropdown = (props: ProductsDropdownProps) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<ProductSummary[]>([]);

  const applicableProducts = useMemo(
    () => products.filter((x) => !props.excludedIDs?.includes(x.id)),
    [products, props.excludedIDs],
  );

  useEffect(() => {
    setLoading(true);
    getProducts({
      page: 0,
      pageSize: 1_000_000,
      search: '',
      includeInactives: true,
      order: 'asc',
      orderBy: 'name',
    })
      .then((res) => setProducts(res.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Autocomplete
        multiple={props.multiple}
        disableCloseOnSelect
        limitTags={2}
        size='small'
        disabled={props.disabled}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            variant='outlined'
            fullWidth
          />
        )}
        options={applicableProducts}
        getOptionLabel={(product) => product.name}
        onChange={(_, val) => {
          if (!val && !props.multiple) {
            props.onChange(undefined);
          }

          const normalized: ProductSummary[] = flatten([val]).filter(
            (x) => !!x,
          );

          if (props.multiple) {
            props.onChange(normalized.map((product) => product!.id));
          } else {
            props.onChange(normalized[0]?.id);
          }
        }}
        value={
          props.multiple
            ? products.filter((product) => props.value.includes(product.id))
            : products.find((product) => product.id === props.value) ?? null
        }
      />
      {loading && <CircularProgress size={24} />}
    </Box>
  );
};
